import React, { useState } from 'react';
import {
  CreditCard, TrendingDown, AlertTriangle, CheckCircle,
  Clock, Building2, Car, GraduationCap, Home, DollarSign, Shield,
  ChevronDown, ChevronUp, FileText,
  AlertCircle, Info, Calendar, Briefcase, MapPin, User,
  ArrowLeft, Download, Loader2
} from 'lucide-react';
import jsPDF from 'jspdf';

// Score color helper
const getScoreColor = (score) => {
  if (score >= 740) return { bg: 'bg-emerald-500', text: 'text-emerald-500', ring: 'ring-emerald-500', gradient: 'from-emerald-500 to-emerald-600', label: 'Excellent' };
  if (score >= 670) return { bg: 'bg-sky-500', text: 'text-sky-500', ring: 'ring-sky-500', gradient: 'from-sky-500 to-sky-600', label: 'Good' };
  if (score >= 580) return { bg: 'bg-amber-500', text: 'text-amber-500', ring: 'ring-amber-500', gradient: 'from-amber-500 to-amber-600', label: 'Fair' };
  return { bg: 'bg-red-500', text: 'text-red-500', ring: 'ring-red-500', gradient: 'from-red-500 to-red-600', label: 'Poor' };
};

// Format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined || amount === 'N/A' || amount === '') return 'N/A';
  const num = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]/g, '')) : amount;
  if (isNaN(num)) return 'N/A';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(num);
};

// Bureau brand colors and logos
const bureauConfig = {
  experian: {
    name: 'Experian',
    color: 'from-blue-600 to-blue-700',
    bgColor: 'bg-blue-600',
    textColor: 'text-blue-600',
    borderColor: 'border-blue-500',
    lightBg: 'bg-blue-50 dark:bg-blue-500/10',
    scoreModel: 'FICO 2',
    icon: '📊'
  },
  equifax: {
    name: 'Equifax',
    color: 'from-red-600 to-red-700',
    bgColor: 'bg-red-600',
    textColor: 'text-red-600',
    borderColor: 'border-red-500',
    lightBg: 'bg-red-50 dark:bg-red-500/10',
    scoreModel: 'FICO 5',
    icon: '📈'
  },
  transunion: {
    name: 'TransUnion',
    color: 'from-sky-500 to-cyan-600',
    bgColor: 'bg-sky-500',
    textColor: 'text-sky-500',
    borderColor: 'border-sky-500',
    lightBg: 'bg-sky-50 dark:bg-sky-500/10',
    scoreModel: 'FICO 4',
    icon: '📉'
  }
};

// Bureau Card Component
const BureauCard = ({ bureauKey, report, summary, onSelect, isSelected }) => {
  const config = bureauConfig[bureauKey];
  if (!config) return null;

  const scoreData = report?.full_feed?.credit_score || {};
  const scoreKey = Object.keys(scoreData)[0];
  const score = scoreData[scoreKey]?.score || 0;
  const scoreNum = parseInt(score) || 0;
  const scoreInfo = getScoreColor(scoreNum);
  const status = report?.status || 'unknown';
  const isSuccess = status === 'success';

  return (
    <button
      onClick={() => onSelect(bureauKey)}
      className={`relative overflow-hidden rounded-2xl border-2 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl ${
        isSelected
          ? `${config.borderColor} shadow-lg ring-2 ${scoreInfo.ring}/30`
          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
    >
      {/* Header with gradient */}
      <div className={`bg-gradient-to-r ${config.color} p-4 text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-2xl">{config.icon}</span>
            <span className="font-bold text-lg">{config.name}</span>
          </div>
          {isSuccess ? (
            <CheckCircle className="w-5 h-5 text-white/80" />
          ) : (
            <AlertCircle className="w-5 h-5 text-white/80" />
          )}
        </div>
        <p className="text-xs text-white/70 mt-1">{config.scoreModel}</p>
      </div>

      {/* Score Display */}
      <div className="p-6 dark:bg-slate-800/50 bg-white">
        {isSuccess ? (
          <>
            <div className="flex items-center justify-center mb-3">
              <div className="relative">
                <svg className="w-28 h-28 transform -rotate-90" viewBox="0 0 36 36">
                  <path
                    className="dark:stroke-slate-700 stroke-slate-200"
                    strokeWidth="3"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className={scoreInfo.text}
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={`${(scoreNum / 850) * 100}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    style={{ stroke: 'currentColor' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-3xl font-bold ${scoreInfo.text}`}>{scoreNum}</span>
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">{config.scoreModel}</span>
                </div>
              </div>
            </div>
            <div className="text-center">
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${scoreInfo.bg} text-white`}>
                {scoreInfo.label}
              </span>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <AlertCircle className="w-8 h-8 mb-2" />
            <p className="text-sm">Report unavailable</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t dark:border-slate-700 border-slate-200 dark:bg-slate-900/50 bg-slate-50">
        <div className="flex items-center justify-center gap-2 text-sm font-medium dark:text-slate-400 text-slate-600">
          <FileText className="w-4 h-4" />
          <span>View Full Report</span>
          <ChevronDown className="w-3 h-3" />
        </div>
      </div>
    </button>
  );
};

// Trade Account Row for Bureau Detail
const BureauTradeAccountRow = ({ account, isExpanded, onToggle }) => {
  const getAccountIcon = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('mortgage') || t.includes('home')) return Home;
    if (t.includes('auto') || t.includes('car')) return Car;
    if (t.includes('student') || t.includes('education')) return GraduationCap;
    return CreditCard;
  };

  const accountType = account.account_type || account.type || 'Account';
  const company = account.company || account.creditor || 'Unknown';
  const accountNumber = account.account_number || '';
  const balance = account.balance || account.current_balance || 0;
  const creditLimit = account.credit_limit || account.high_credit || null;
  const monthlyPayment = account.monthly_payment_amount || account.monthly_payment || 0;
  const accountStatus = account.account_status || account.status || 'Unknown';
  const accountRating = account.account_rating || account.rating || '';
  const dateOpened = account.date_of_opening || account.date_opened || '';
  const lastPaymentDate = account.last_payment_date || '';

  const Icon = getAccountIcon(accountType);
  const isGoodStanding = accountRating?.toLowerCase().includes('good') ||
                         accountRating?.toLowerCase().includes('agreed') ||
                         accountRating?.toLowerCase().includes('paying');

  const late30 = parseInt(account['30_day_delinquencies'] || 0);
  const late60 = parseInt(account['60_day_delinquencies'] || 0);
  const late90 = parseInt(account['90_day_delinquencies'] || 0);

  return (
    <div className="border-b dark:border-slate-700/50 border-slate-200 last:border-0">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center gap-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left"
      >
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isGoodStanding ? 'bg-emerald-100 dark:bg-emerald-500/20' : 'bg-amber-100 dark:bg-amber-500/20'}`}>
          <Icon className={`w-5 h-5 ${isGoodStanding ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-semibold dark:text-white text-slate-900 truncate">{company}</p>
            {isGoodStanding ? (
              <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
            )}
          </div>
          <p className="text-xs text-slate-500">{accountType} {accountNumber ? `• ${accountNumber}` : ''}</p>
        </div>
        <div className="text-right hidden sm:block">
          <p className="font-semibold dark:text-white text-slate-900">{formatCurrency(balance)}</p>
          <p className="text-xs text-slate-500">Balance</p>
        </div>
        <div className="text-right hidden md:block">
          <p className="font-medium dark:text-white text-slate-900">{formatCurrency(monthlyPayment)}</p>
          <p className="text-xs text-slate-500">Payment</p>
        </div>
        {/* Late Payment Indicators */}
        <div className="flex gap-1">
          {late30 > 0 && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-500 text-white">{late30}x30</span>}
          {late60 > 0 && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-orange-500 text-white">{late60}x60</span>}
          {late90 > 0 && <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-500 text-white">{late90}x90</span>}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 pt-2 dark:bg-slate-800/30 bg-slate-50 animate-fadeIn">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-xs text-slate-500 mb-1">Account Status</p>
              <p className={`font-medium ${accountStatus?.toLowerCase() === 'open' ? 'text-emerald-500' : 'text-slate-400'}`}>
                {accountStatus}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Account Rating</p>
              <p className={`font-medium text-xs ${isGoodStanding ? 'text-emerald-500' : 'text-amber-500'}`}>
                {accountRating || 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Date Opened</p>
              <p className="dark:text-white text-slate-900">{dateOpened || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Last Payment</p>
              <p className="dark:text-white text-slate-900">{lastPaymentDate || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Credit Limit / High</p>
              <p className="dark:text-white text-slate-900">{formatCurrency(creditLimit)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Terms</p>
              <p className="dark:text-white text-slate-900">{account.terms || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Responsibility</p>
              <p className="dark:text-white text-slate-900 capitalize">{account.responsibility?.toLowerCase() || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Past Due</p>
              <p className={`font-medium ${parseInt(account.past_due || 0) > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                {formatCurrency(account.past_due || 0)}
              </p>
            </div>
          </div>
          {(account.remarks || account.narrative) && account.remarks !== 'N/A' && (
            <div className="mt-3 p-3 rounded-lg dark:bg-slate-700/50 bg-slate-100 flex items-start gap-2">
              <Info className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-slate-500 dark:text-slate-400">{account.remarks || account.narrative}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Full Bureau Report Detail View
const BureauDetailView = ({ bureauKey, report, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Generate and download PDF report
  const generatePdfReport = () => {
    setDownloadingPdf(true);

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let yPos = 20;

      // Helper function to add new page if needed
      const checkPageBreak = (neededSpace = 30) => {
        if (yPos + neededSpace > 270) {
          doc.addPage();
          yPos = 20;
        }
      };

      // Bureau colors
      const colors = {
        experian: [37, 99, 235],    // Blue
        equifax: [220, 38, 38],     // Red
        transunion: [14, 165, 233]  // Cyan
      };
      const bureauColor = colors[bureauKey] || [37, 99, 235];

      // Header with bureau branding
      doc.setFillColor(...bureauColor);
      doc.rect(0, 0, pageWidth, 45, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text(config.name.toUpperCase(), margin, 25);

      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Credit Report', margin, 35);

      // Score circle on header
      doc.setFillColor(255, 255, 255);
      doc.circle(pageWidth - 35, 25, 18, 'F');
      doc.setTextColor(...bureauColor);
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text(String(scoreNum), pageWidth - 35, 28, { align: 'center' });
      doc.setFontSize(8);
      doc.text(config.scoreModel, pageWidth - 35, 35, { align: 'center' });

      yPos = 60;

      // Credit Score Summary
      doc.setTextColor(30, 41, 59);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Credit Score Summary', margin, yPos);
      yPos += 10;

      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin, yPos, pageWidth - 2 * margin, 25, 3, 3, 'F');

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(71, 85, 105);
      doc.text(`Score: ${scoreNum}`, margin + 10, yPos + 10);
      doc.text(`Rating: ${scoreInfo.label}`, margin + 60, yPos + 10);
      doc.text(`Model: ${config.scoreModel}`, margin + 110, yPos + 10);
      yPos += 35;

      // Score Factors
      if (factors.length > 0) {
        checkPageBreak(50);
        doc.setTextColor(30, 41, 59);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Score Factors', margin, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);
        factors.forEach((factor, idx) => {
          checkPageBreak(8);
          doc.text(`${idx + 1}. ${factor}`, margin + 5, yPos);
          yPos += 7;
        });
        yPos += 10;
      }

      // Trade Accounts Summary
      if (tradeAccounts.length > 0) {
        checkPageBreak(60);
        doc.setTextColor(30, 41, 59);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Trade Accounts (${tradeAccounts.length})`, margin, yPos);
        yPos += 10;

        // Table header
        doc.setFillColor(...bureauColor);
        doc.rect(margin, yPos, pageWidth - 2 * margin, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.text('Creditor', margin + 3, yPos + 5.5);
        doc.text('Type', margin + 55, yPos + 5.5);
        doc.text('Balance', margin + 95, yPos + 5.5);
        doc.text('Payment', margin + 130, yPos + 5.5);
        doc.text('Status', margin + 155, yPos + 5.5);
        yPos += 10;

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(51, 65, 85);

        tradeAccounts.slice(0, 15).forEach((acc, idx) => {
          checkPageBreak(8);
          const bgColor = idx % 2 === 0 ? [248, 250, 252] : [255, 255, 255];
          doc.setFillColor(...bgColor);
          doc.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F');

          const creditor = (acc.company || acc.creditor || 'Unknown').substring(0, 20);
          const type = (acc.account_type || acc.type || 'N/A').substring(0, 15);
          const balance = formatCurrency(acc.balance || acc.current_balance || 0);
          const payment = formatCurrency(acc.monthly_payment_amount || acc.monthly_payment || 0);
          const status = (acc.account_status || acc.status || 'N/A').substring(0, 10);

          doc.setFontSize(8);
          doc.text(creditor, margin + 3, yPos);
          doc.text(type, margin + 55, yPos);
          doc.text(balance, margin + 95, yPos);
          doc.text(payment, margin + 130, yPos);
          doc.text(status, margin + 155, yPos);
          yPos += 8;
        });

        if (tradeAccounts.length > 15) {
          doc.setFontSize(8);
          doc.setTextColor(100, 116, 139);
          doc.text(`... and ${tradeAccounts.length - 15} more accounts`, margin + 3, yPos);
          yPos += 8;
        }
        yPos += 10;
      }

      // Inquiries
      if (inquiries.length > 0) {
        checkPageBreak(40);
        doc.setTextColor(30, 41, 59);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Credit Inquiries (${inquiries.length})`, margin, yPos);
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(71, 85, 105);
        inquiries.slice(0, 10).forEach((inq) => {
          checkPageBreak(7);
          doc.text(`• ${inq.company || 'Unknown'} - ${inq.date || 'N/A'} (${inq.industry || 'Other'})`, margin + 5, yPos);
          yPos += 6;
        });
        yPos += 10;
      }

      // Collections
      if (collections.length > 0) {
        checkPageBreak(40);
        doc.setTextColor(220, 38, 38);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(`Collections (${collections.length})`, margin, yPos);
        yPos += 10;

        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        collections.forEach((coll) => {
          checkPageBreak(15);
          const status = coll.status || 'Unknown';
          const isPaid = status.toLowerCase().includes('paid');
          doc.setTextColor(isPaid ? 34 : 220, isPaid ? 197 : 38, isPaid ? 94 : 38);
          doc.text(`• ${coll.company || 'Unknown'} - ${formatCurrency(coll.balance || 0)} (${status})`, margin + 5, yPos);
          yPos += 6;
        });
        yPos += 10;
      }

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, margin, 285);
      doc.text('This report is for informational purposes only.', pageWidth - margin, 285, { align: 'right' });

      // Save the PDF
      doc.save(`${config.name}_Credit_Report.pdf`);

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const config = bureauConfig[bureauKey];
  if (!config || !report) return null;

  const fullFeed = report.full_feed || {};
  const personalInfo = fullFeed.personal_information || {};
  const scoreData = fullFeed.credit_score || {};
  const scoreKey = Object.keys(scoreData)[0];
  const creditScore = scoreData[scoreKey] || {};
  const scoreNum = parseInt(creditScore.score) || 0;
  const scoreInfo = getScoreColor(scoreNum);
  const factors = creditScore.factors || [];

  const addressHistory = fullFeed.address_history || {};
  const employmentHistory = fullFeed.employment_history || {};
  const identityRisk = fullFeed.identity_risk || {};
  const tradeAccounts = fullFeed.trade_accounts || [];
  const inquiries = fullFeed.inquiries || [];
  const collections = fullFeed.collections || [];
  const publicRecords = fullFeed.public_record || [];
  const statements = fullFeed.statements || [];

  const toggleAccount = (idx) => {
    setExpandedAccounts(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: CreditCard },
    { id: 'accounts', label: `Accounts (${tradeAccounts.length})`, icon: Building2 },
    { id: 'inquiries', label: `Inquiries (${inquiries.length})`, icon: Clock },
    { id: 'collections', label: 'Collections', icon: AlertTriangle },
    { id: 'personal', label: 'Personal', icon: User },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`bg-gradient-to-r ${config.color} p-6 text-white relative overflow-hidden`}>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24" />

        <div className="relative">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Bureau Selection</span>
          </button>

          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">{config.icon}</span>
                <h2 className="text-2xl font-bold">{config.name}</h2>
              </div>
              <p className="text-white/70 text-sm">{config.scoreModel} Credit Report</p>
              <p className="text-white/60 text-xs mt-1">{personalInfo.name || 'Applicant Report'}</p>
            </div>

            <div className="text-right">
              <div className="relative inline-flex items-center justify-center">
                <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 36 36">
                  <path
                    className="stroke-white/20"
                    strokeWidth="3"
                    fill="none"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className="stroke-white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    fill="none"
                    strokeDasharray={`${(scoreNum / 850) * 100}, 100`}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-white">{scoreNum}</span>
                </div>
              </div>
              <p className="text-white/70 text-sm mt-1">{scoreInfo.label}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b dark:border-slate-700 border-slate-200 dark:bg-slate-900 bg-white px-4">
        <div className="flex gap-1 overflow-x-auto py-2">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === id
                  ? `${config.bgColor} text-white`
                  : 'dark:text-slate-400 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-6 dark:bg-slate-950 bg-slate-50">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Score Factors */}
            {factors.length > 0 && (
              <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
                <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
                  <TrendingDown className="w-5 h-5 text-amber-500" />
                  Score Factors
                </h3>
                <div className="space-y-2">
                  {factors.map((factor, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 rounded-xl dark:bg-slate-800/50 bg-slate-50">
                      <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-bold text-amber-500">{idx + 1}</span>
                      </div>
                      <p className="text-sm dark:text-slate-300 text-slate-700">{factor}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Identity Risk */}
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
              <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
                <Shield className="w-5 h-5 dark:text-sky-400 text-sky-600" />
                Identity & Security
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 text-center">
                  <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${identityRisk.fraud_alert ? 'bg-red-500/20' : 'bg-emerald-500/20'}`}>
                    {identityRisk.fraud_alert ? (
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500">Fraud Alert</p>
                  <p className={`font-semibold ${identityRisk.fraud_alert ? 'text-red-500' : 'text-emerald-500'}`}>
                    {identityRisk.fraud_alert ? 'Active' : 'None'}
                  </p>
                </div>
                <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 text-center">
                  <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${identityRisk.security_freeze ? 'bg-amber-500/20' : 'bg-emerald-500/20'}`}>
                    {identityRisk.security_freeze ? (
                      <Shield className="w-5 h-5 text-amber-500" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500">Security Freeze</p>
                  <p className={`font-semibold ${identityRisk.security_freeze ? 'text-amber-500' : 'text-emerald-500'}`}>
                    {identityRisk.security_freeze ? 'Active' : 'None'}
                  </p>
                </div>
                {identityRisk.identity_verification && (
                  <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 text-center">
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center bg-emerald-500/20">
                      <CheckCircle className="w-5 h-5 text-emerald-500" />
                    </div>
                    <p className="text-xs text-slate-500">Identity</p>
                    <p className="font-semibold text-emerald-500">{identityRisk.identity_verification}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold dark:text-white text-slate-900">{tradeAccounts.length}</p>
                <p className="text-xs text-slate-500">Trade Accounts</p>
              </div>
              <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold dark:text-white text-slate-900">{inquiries.length}</p>
                <p className="text-xs text-slate-500">Inquiries</p>
              </div>
              <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-4 text-center">
                <p className={`text-3xl font-bold ${collections.length > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{collections.length}</p>
                <p className="text-xs text-slate-500">Collections</p>
              </div>
              <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-xl p-4 text-center">
                <p className={`text-3xl font-bold ${publicRecords.length > 0 ? 'text-red-500' : 'text-emerald-500'}`}>{publicRecords.length}</p>
                <p className="text-xs text-slate-500">Public Records</p>
              </div>
            </div>

            {/* AI Report Download */}
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
              <button
                onClick={generatePdfReport}
                disabled={downloadingPdf}
                className={`w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl ${config.bgColor} text-white hover:opacity-90 transition-all disabled:opacity-50 text-lg font-semibold`}
              >
                {downloadingPdf ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Download className="w-5 h-5" />
                )}
                {downloadingPdf ? 'Generating...' : 'Download'}
              </button>
            </div>
          </div>
        )}

        {/* Accounts Tab */}
        {activeTab === 'accounts' && (
          <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl overflow-hidden">
            <div className="p-4 border-b dark:border-slate-800 border-slate-200">
              <h3 className="text-lg font-semibold dark:text-white text-slate-900">
                Trade Accounts ({tradeAccounts.length})
              </h3>
            </div>
            <div className="max-h-[500px] overflow-y-auto">
              {tradeAccounts.map((account, idx) => (
                <BureauTradeAccountRow
                  key={idx}
                  account={account}
                  isExpanded={expandedAccounts[idx]}
                  onToggle={() => toggleAccount(idx)}
                />
              ))}
              {tradeAccounts.length === 0 && (
                <div className="p-8 text-center text-slate-500">No trade accounts found</div>
              )}
            </div>
          </div>
        )}

        {/* Inquiries Tab */}
        {activeTab === 'inquiries' && (
          <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
            <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4">
              Credit Inquiries ({inquiries.length})
            </h3>
            <div className="space-y-2">
              {inquiries.map((inquiry, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                      (inquiry.industry || '').toLowerCase().includes('mortgage') ? 'bg-sky-500/20 text-sky-500' :
                      (inquiry.industry || '').toLowerCase().includes('auto') ? 'bg-violet-500/20 text-violet-500' :
                      'bg-slate-500/20 text-slate-500'
                    }`}>
                      {inquiry.industry || 'Other'}
                    </div>
                    <span className="font-medium dark:text-white text-slate-900">{inquiry.company}</span>
                  </div>
                  <span className="text-sm text-slate-500 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {inquiry.date}
                  </span>
                </div>
              ))}
              {inquiries.length === 0 && (
                <div className="text-center py-8 text-slate-500">No recent inquiries</div>
              )}
            </div>
          </div>
        )}

        {/* Collections Tab */}
        {activeTab === 'collections' && (
          <div className="space-y-4">
            {collections.length === 0 ? (
              <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-8 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="font-semibold dark:text-white text-slate-900">No Collections</p>
                <p className="text-sm text-slate-500">No collection accounts found on this report</p>
              </div>
            ) : (
              collections.map((collection, idx) => {
                const isPaid = (collection.status || '').toLowerCase().includes('paid');
                return (
                  <div key={idx} className={`rounded-2xl p-6 border ${isPaid ? 'dark:border-emerald-500/30 border-emerald-200 dark:bg-emerald-500/10 bg-emerald-50' : 'dark:border-red-500/30 border-red-200 dark:bg-red-500/10 bg-red-50'}`}>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="font-semibold dark:text-white text-slate-900">{collection.company}</p>
                        <p className="text-xs text-slate-500">Original: {collection.original_creditor}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${isPaid ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                        {collection.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-slate-500">Original Amount</p>
                        <p className="font-semibold dark:text-white text-slate-900">{formatCurrency(collection.original_amount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Current Balance</p>
                        <p className="font-semibold dark:text-white text-slate-900">{formatCurrency(collection.balance)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Date</p>
                        <p className="font-semibold dark:text-white text-slate-900">{collection.date_paid || collection.date_opened}</p>
                      </div>
                    </div>
                    {collection.remarks && (
                      <p className="mt-3 text-xs text-slate-500 italic">{collection.remarks}</p>
                    )}
                  </div>
                );
              })
            )}

            {/* Statements */}
            {statements.length > 0 && (
              <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6 mt-4">
                <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4">Consumer Statements</h3>
                {statements.map((stmt, idx) => (
                  <div key={idx} className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 mb-2 last:mb-0">
                    <p className="text-xs text-slate-500 mb-1">Filed: {stmt.date_filed}</p>
                    <p className="text-sm dark:text-slate-300 text-slate-700">{stmt.statement}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Public Records */}
            {publicRecords.length > 0 && (
              <div className="dark:bg-slate-900 bg-white border dark:border-red-500/30 border-red-200 rounded-2xl p-6 mt-4">
                <h3 className="text-lg font-semibold text-red-500 flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5" />
                  Public Records
                </h3>
                {publicRecords.map((record, idx) => (
                  <div key={idx} className="p-4 rounded-xl bg-red-50 dark:bg-red-500/10 mb-2 last:mb-0">
                    <p className="font-semibold dark:text-white text-slate-900">{record.type || 'Public Record'}</p>
                    <p className="text-sm text-slate-500">{JSON.stringify(record)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Personal Tab */}
        {activeTab === 'personal' && (
          <div className="space-y-6">
            {/* Personal Info */}
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
              <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
                <User className="w-5 h-5 dark:text-sky-400 text-sky-600" />
                Personal Information
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Name</p>
                  <p className="font-medium dark:text-white text-slate-900">{personalInfo.name || 'N/A'}</p>
                </div>
                {personalInfo.aka && (
                  <div>
                    <p className="text-xs text-slate-500">Also Known As</p>
                    <p className="font-medium dark:text-white text-slate-900">{personalInfo.aka}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Address History */}
            <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
              <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 dark:text-sky-400 text-sky-600" />
                Address History
              </h3>
              <div className="space-y-3">
                {addressHistory.current?.map((addr, idx) => (
                  <div key={`current-${idx}`} className="p-4 rounded-xl dark:bg-emerald-500/10 bg-emerald-50 border dark:border-emerald-500/30 border-emerald-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-500 text-white">Current</span>
                      {addr.possession_type && <span className="text-xs text-slate-500">{addr.possession_type}</span>}
                    </div>
                    <p className="font-medium dark:text-white text-slate-900">{addr.street}</p>
                    <p className="text-sm text-slate-500">{addr.city}, {addr.state} {addr.zip}</p>
                    <p className="text-xs text-slate-400 mt-1">Reported: {addr.date_reported}</p>
                  </div>
                ))}
                {addressHistory.previous?.map((addr, idx) => (
                  <div key={`prev-${idx}`} className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium dark:bg-slate-700 bg-slate-200 dark:text-slate-300 text-slate-600">Previous</span>
                    </div>
                    <p className="font-medium dark:text-white text-slate-900">{addr.street}</p>
                    <p className="text-sm text-slate-500">{addr.city}, {addr.state} {addr.zip}</p>
                    <p className="text-xs text-slate-400 mt-1">Reported: {addr.date_reported}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Employment History */}
            {(employmentHistory.current || employmentHistory.previous?.length > 0) && (
              <div className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
                <h3 className="text-lg font-semibold dark:text-white text-slate-900 flex items-center gap-2 mb-4">
                  <Briefcase className="w-5 h-5 dark:text-sky-400 text-sky-600" />
                  Employment History
                </h3>
                <div className="space-y-3">
                  {employmentHistory.current && (
                    <div className="p-4 rounded-xl dark:bg-emerald-500/10 bg-emerald-50 border dark:border-emerald-500/30 border-emerald-200">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-500 text-white">Current</span>
                      </div>
                      <p className="font-medium dark:text-white text-slate-900">{employmentHistory.current.employer}</p>
                      <p className="text-sm text-slate-500">{employmentHistory.current.position}</p>
                      {employmentHistory.current.income && (
                        <p className="text-sm font-medium text-emerald-600 dark:text-emerald-400 mt-1">
                          {formatCurrency(employmentHistory.current.income)}/year
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">Since: {employmentHistory.current.date_hired}</p>
                    </div>
                  )}
                  {employmentHistory.previous?.map((emp, idx) => (
                    <div key={idx} className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50">
                      <p className="font-medium dark:text-white text-slate-900">{emp.employer}</p>
                      <p className="text-sm text-slate-500">{emp.position}</p>
                      <p className="text-xs text-slate-400 mt-1">{emp.date_hired} - {emp.date_left}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Bureau Report Section Component
const BureauReportSection = ({ reports, summary }) => {
  const [selectedBureau, setSelectedBureau] = useState(null);

  if (selectedBureau && reports[selectedBureau]) {
    return (
      <BureauDetailView
        bureauKey={selectedBureau}
        report={reports[selectedBureau]}
        onBack={() => setSelectedBureau(null)}
      />
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold dark:text-white text-slate-900 mb-2">Credit Bureau Reports</h2>
        <p className="text-sm text-slate-500">Click on a bureau card to view the complete credit report</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {['experian', 'equifax', 'transunion'].map((bureauKey) => (
          <BureauCard
            key={bureauKey}
            bureauKey={bureauKey}
            report={reports[bureauKey]}
            summary={summary}
            onSelect={setSelectedBureau}
            isSelected={selectedBureau === bureauKey}
          />
        ))}
      </div>

      {/* Summary Stats */}
      <div className="mt-8 dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl p-6">
        <h3 className="text-lg font-semibold dark:text-white text-slate-900 mb-4">Tri-Merge Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 text-center">
            <p className="text-3xl font-bold dark:text-sky-400 text-sky-600">
              {summary?.credit_scores?.middle_score || summary?.credit_scores?.average || '—'}
            </p>
            <p className="text-xs text-slate-500">Middle Score</p>
          </div>
          <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 text-center">
            <p className="text-3xl font-bold dark:text-white text-slate-900">
              {summary?.payment_history?.total_accounts || '—'}
            </p>
            <p className="text-xs text-slate-500">Total Accounts</p>
          </div>
          <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 text-center">
            <p className="text-3xl font-bold dark:text-amber-400 text-amber-600">
              {formatCurrency(summary?.total_debt?.total)}
            </p>
            <p className="text-xs text-slate-500">Total Debt</p>
          </div>
          <div className="p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 text-center">
            <p className={`text-3xl font-bold ${(summary?.credit_utilization?.utilization_percentage || 0) > 30 ? 'text-amber-500' : 'text-emerald-500'}`}>
              {summary?.credit_utilization?.utilization_percentage?.toFixed(1) || 0}%
            </p>
            <p className="text-xs text-slate-500">Utilization</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BureauReportSection;
